// Dépôt des 3 CSV hebdomadaires + écran de validation du diff.
//
// Le parcours est volontairement en deux temps : on ne peut PAS appliquer sans
// avoir vu le diff. Les changements critiques (ceux qui pilotent les alertes) et
// le nombre de comptes utilisateurs impactés sont mis en avant, parce que c'est
// sur eux que porte la décision.
//
// Textes en dur, en français : cette page admin ne passe pas par l'i18n (ni le
// reste d'AdminPropFirmsPage), elle n'est pas exposée aux utilisateurs.

import { useState } from "react";
import Papa from "papaparse";
import {
  previewPropFirmReferentialImport,
  applyPropFirmReferentialImport,
  useAction,
} from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../client/components/ui/dialog";
import { Button } from "../../../client/components/ui/button";
import { Badge } from "../../../client/components/ui/badge";
import { useToast } from "../../../client/hooks/use-toast";
import { Upload, AlertTriangle, ShieldAlert, FileWarning, Check } from "lucide-react";

type Row = Record<string, string>;
type FileKind = "firms" | "tiers" | "phases";

interface DiffChange {
  kind: "create" | "update" | "delete";
  entity: string;
  key: string;
  firmSlug: string;
  field?: string;
  before?: unknown;
  after?: unknown;
  critical: boolean;
  impactedAccounts?: number;
}

interface UnverifiedField {
  firmSlug: string;
  key: string;
  field: string;
  wasVerified: boolean;
}

interface PreviewResult {
  ok: boolean;
  errors: { file: string; line: number; message: string }[];
  counts: { firms: number; tiers: number; phases: number };
  changes: DiffChange[];
  unverified: UnverifiedField[];
  totalImpactedAccounts: number;
}

const FILE_LABELS: Record<FileKind, string> = {
  firms: "prop_firms.csv",
  tiers: "prop_firm_tiers.csv",
  phases: "prop_firm_phases.csv",
};

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

export default function ReferentialImportDialog({ open, onOpenChange, onImported }: Props) {
  const preview = useAction(previewPropFirmReferentialImport);
  const apply = useAction(applyPropFirmReferentialImport);
  const { toast } = useToast();

  const [rows, setRows] = useState<Record<FileKind, Row[]>>({ firms: [], tiers: [], phases: [] });
  const [names, setNames] = useState<Record<FileKind, string>>({ firms: "", tiers: "", phases: "" });
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Les suppressions de phases ne s'appliquent QUE si elles sont cochées ici.
  const [acceptedDeletions, setAcceptedDeletions] = useState<Set<string>>(new Set());

  const handleFile = (kind: FileKind) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNames((n) => ({ ...n, [kind]: file.name }));
    setResult(null);
    Papa.parse<Row>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => setRows((r) => ({ ...r, [kind]: res.data })),
    });
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const res = (await preview(rows)) as unknown as PreviewResult;
      setResult(res);
      setAcceptedDeletions(new Set());
      if (!res.ok) {
        toast({ title: `${res.errors.length} erreur(s) dans les CSV`, variant: "destructive" });
      } else if (res.changes.length === 0) {
        toast({ title: "Aucun changement — le référentiel est déjà à jour" });
      }
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    setBusy(true);
    try {
      const res = (await apply({ ...rows, acceptedDeletions: [...acceptedDeletions] })) as any;
      toast({
        title: `Appliqué : ${res.firmsUpserted} firmes, ${res.tiersUpserted} paliers, ${res.phasesUpserted} phases` +
          (res.phasesDeleted ? `, ${res.phasesDeleted} phase(s) supprimée(s)` : ""),
      });
      onImported();
      onOpenChange(false);
      setResult(null);
      setRows({ firms: [], tiers: [], phases: [] });
      setNames({ firms: "", tiers: "", phases: "" });
    } catch (e) {
      toast({ title: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const toggleDeletion = (key: string) => {
    setAcceptedDeletions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hasAllFiles = rows.firms.length > 0 && rows.tiers.length > 0 && rows.phases.length > 0;
  const critical = result?.changes.filter((c) => c.critical) ?? [];
  const cosmetic = result?.changes.filter((c) => !c.critical) ?? [];
  const deletions = result?.changes.filter((c) => c.kind === "delete") ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importer le référentiel prop firms</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Dépôt des 3 fichiers */}
          <div className="grid gap-2">
            {(Object.keys(FILE_LABELS) as FileKind[]).map((kind) => (
              <div key={kind} className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm border rounded-md px-3 py-2 hover:bg-accent w-64">
                  <Upload className="w-4 h-4" />
                  <span className="font-mono text-xs">{FILE_LABELS[kind]}</span>
                  <input type="file" accept=".csv" className="hidden" onChange={handleFile(kind)} />
                </label>
                <span className="text-xs text-muted-foreground">
                  {names[kind]
                    ? `${names[kind]} — ${rows[kind].length} lignes`
                    : "aucun fichier"}
                </span>
              </div>
            ))}
          </div>

          {/* Erreurs de format : bloquantes */}
          {result && !result.ok && (
            <div className="border border-destructive/40 rounded-md p-3 space-y-1">
              <p className="text-sm font-medium flex items-center gap-2 text-destructive">
                <FileWarning className="w-4 h-4" />
                {result.errors.length} erreur(s) — rien ne sera appliqué
              </p>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs font-mono text-muted-foreground">
                    [{e.file}
                    {e.line > 0 ? `:${e.line}` : ""}] {e.message}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Diff */}
          {result?.ok && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline">{result.counts.firms} firmes</Badge>
                <Badge variant="outline">{result.counts.tiers} paliers</Badge>
                <Badge variant="outline">{result.counts.phases} phases</Badge>
                {result.changes.length === 0 && (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Check className="w-4 h-4" /> Aucun changement
                  </span>
                )}
              </div>

              {result.totalImpactedAccounts > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-3">
                  <p className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span>
                      <strong>{result.totalImpactedAccounts}</strong> compte(s) utilisateur non gelé(s)
                      dépendent des paliers modifiés. Les comptes gelés ne sont pas affectés.
                    </span>
                  </p>
                </div>
              )}

              {critical.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-destructive" />
                    {critical.length} changement(s) de règles
                  </h3>
                  <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                    {critical.map((c, i) => (
                      <ChangeRow
                        key={i}
                        change={c}
                        checked={acceptedDeletions.has(c.key)}
                        onToggle={() => toggleDeletion(c.key)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {cosmetic.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    {cosmetic.length} changement(s) sans effet sur les alertes
                  </h3>
                  <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                    {cosmetic.map((c, i) => (
                      <ChangeRow key={i} change={c} checked={false} onToggle={() => {}} />
                    ))}
                  </div>
                </section>
              )}

              {result.unverified.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    {result.unverified.length} champ(s) non vérifié(s)
                  </h3>
                  <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                    {result.unverified.map((u, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                        {u.wasVerified && (
                          <Badge variant="destructive" className="text-[10px]">
                            régression
                          </Badge>
                        )}
                        <span className="font-mono">{u.key}</span>
                        <span className="text-muted-foreground">· {u.field}</span>
                        {u.wasVerified && (
                          <span className="text-muted-foreground">
                            (une valeur vérifiée existe en base et serait perdue)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {deletions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Les suppressions de phases ne sont appliquées que si elles sont cochées.
                  Non cochées, les phases existantes sont conservées.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Fermer
          </Button>
          <Button onClick={runPreview} disabled={!hasAllFiles || busy} variant="secondary">
            {busy ? "..." : "Prévisualiser"}
          </Button>
          <Button
            onClick={runApply}
            disabled={!result?.ok || busy || result.changes.length === 0}
          >
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRow({
  change,
  checked,
  onToggle,
}: {
  change: DiffChange;
  checked: boolean;
  onToggle: () => void;
}) {
  const isDeletion = change.kind === "delete";

  return (
    <div className="px-3 py-2 text-xs flex items-start gap-2">
      {isDeletion && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5"
          aria-label={`Confirmer la suppression de ${change.key}`}
        />
      )}
      <Badge
        variant={isDeletion ? "destructive" : change.kind === "create" ? "secondary" : "outline"}
        className="text-[10px] shrink-0"
      >
        {change.kind === "create" ? "ajout" : isDeletion ? "suppression" : "modif"}
      </Badge>
      <div className="min-w-0 flex-1">
        <p className="font-mono truncate">{change.key}</p>
        {change.field && (
          <p className="text-muted-foreground">
            {change.field} : <span className="font-mono">{fmt(change.before)}</span> →{" "}
            <span className="font-mono font-medium">{fmt(change.after)}</span>
          </p>
        )}
      </div>
      {change.impactedAccounts ? (
        <Badge variant="outline" className="text-[10px] shrink-0">
          {change.impactedAccounts} compte(s)
        </Badge>
      ) : null}
    </div>
  );
}
